import type { NoteFx } from "./types";
import type { VoiceNoteFxParams } from "../types";

export type DecodeNoteFxOptions = {
  /** Step length in seconds — used to pace arpeggio (~one full cycle per step). */
  stepSec?: number;
};

/**
 * Decode tracker row FX into kernel params (host-side).
 *
 * | FX | Value | Meaning |
 * |----|-------|---------|
 * | Vxy | hi=speed, lo=depth | Vibrato Hz / semitones |
 * | Uxx | rate | Slide up (semitones/sec) |
 * | Dxx | rate | Slide down |
 * | Pxx | rate | Portamento toward note |
 * | Axy | hi=semi1, lo=semi2 | Arpeggio (0 = skip that stage) |
 * | Fxx | 00–FF | Cutoff offset (−2..+2 octaves; 80 ≈ 0) |
 * | Bxx | 00–FF | Voice pan (−1..+1; 00=L, 80=C, FF=R). Omit FX → slot pan |
 * | Cxx / C-- | ticks / hold | Gate length — host PatternPlayer (`C--` = open hold). With `Gxy`, domain is cut window or unbounded |
 * | Rxy | hi=decay, lo=hits | Retrigger / ratchet — PatternPlayer host-only |
 * | Gxy | hi=duty, lo=slices | Gate / stutter — PatternPlayer host-only. Period = domain/slices; `C--+G` repeats forever |
 * | ?xx | 00–99 | Chance % to play note — PatternPlayer host-only (decimal) |
 * | Exx | 00–FF | Note delay onset (`xx/256` of a step) — PatternPlayer host-only |
 */

/** Map tracker `Bxx` byte to pan −1..1 (`B00` L, `B80` C, `BFF` R). */
export function decodePanByte(value: number): number {
  const v = value & 0xff;
  if (v <= 0x80) {
    return (v - 0x80) / 0x80;
  }
  return (v - 0x80) / (0xff - 0x80);
}

export function decodeNoteFx(
  fx: NoteFx[] | undefined,
  options: DecodeNoteFxOptions = {},
): { params: VoiceNoteFxParams } {
  const params: VoiceNoteFxParams = {};

  if (!fx || fx.length === 0) {
    return { params };
  }

  for (const item of fx) {
    const v = item.value & 0xff;
    switch (item.kind) {
      case "vibrato": {
        const speedNibble = (v >> 4) & 0xf;
        const depthNibble = v & 0xf;
        // Keep prior depth/speed when nibble is 0 (classic memory) — caller merges.
        if (speedNibble > 0) {
          params.vibratoSpeedHz = speedNibble * 1.25;
        }
        if (depthNibble > 0) {
          params.vibratoDepthSemi = depthNibble * 0.35;
        }
        break;
      }
      case "slideUp":
        params.slideSemiPerSec = Math.max(0.5, v) * 0.55;
        break;
      case "slideDown":
        params.slideSemiPerSec = -Math.max(0.5, v) * 0.55;
        break;
      case "portamento":
        params.portaSemiPerSec = Math.max(0.5, v) * 0.85;
        break;
      case "arpeggio": {
        const semi1 = (v >> 4) & 0xf;
        const semi2 = v & 0xf;
        params.arpSemi1 = semi1;
        params.arpSemi2 = semi2;
        const stages = 1 + (semi1 > 0 ? 1 : 0) + (semi2 > 0 ? 1 : 0);
        if (stages <= 1) {
          params.arpHz = 0;
        } else {
          const stepSec = options.stepSec && options.stepSec > 0 ? options.stepSec : 0.125;
          // One full cycle (base → +x → +y) per step, classic row feel.
          params.arpHz = Math.max(2, stages / stepSec);
        }
        break;
      }
      case "filter":
        // 0x00 → −2 oct, 0x80 → 0, 0xFF → ~+2
        params.cutoffOct = (v / 128) - 1;
        params.cutoffOct *= 2;
        break;
      case "pan":
        params.pan = decodePanByte(v);
        break;
      case "cut":
      case "retrigger":
      case "gate":
      case "noteDelay":
      case "chance":
        // Host scheduling only
        break;
    }
  }

  return { params };
}

/** Merge incoming FX onto existing voice params (FX-only rows). */
export function mergeNoteFxParams(
  prev: VoiceNoteFxParams,
  next: VoiceNoteFxParams,
): VoiceNoteFxParams {
  const out: VoiceNoteFxParams = { ...prev };
  if (next.vibratoSpeedHz !== undefined) {
    out.vibratoSpeedHz = next.vibratoSpeedHz;
  }
  if (next.vibratoDepthSemi !== undefined) {
    out.vibratoDepthSemi = next.vibratoDepthSemi;
  }
  if (next.slideSemiPerSec !== undefined) {
    out.slideSemiPerSec = next.slideSemiPerSec;
  }
  if (next.portaSemiPerSec !== undefined) {
    out.portaSemiPerSec = next.portaSemiPerSec;
  }
  if (next.cutoffOct !== undefined) {
    out.cutoffOct = next.cutoffOct;
  }
  if (next.arpSemi1 !== undefined) {
    out.arpSemi1 = next.arpSemi1;
  }
  if (next.arpSemi2 !== undefined) {
    out.arpSemi2 = next.arpSemi2;
  }
  if (next.arpHz !== undefined) {
    out.arpHz = next.arpHz;
  }
  if (next.pan !== undefined) {
    out.pan = next.pan;
  }
  return out;
}
